import React, { useEffect, useState, useRef } from "react";
import { Bot, Check, X, Plus, AlertCircle, BookOpen, Copy, Trash2, Send, ExternalLink, QrCode, Clock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

function GuideStep({ n, title, children }) {
    return (
        <div className="flex gap-3 pb-4 mb-4" style={{ borderBottom: "1px dashed var(--line-2)" }}>
            <div className="w-7 h-7 rounded-full grid place-items-center flex-shrink-0 mono text-[12px] font-bold"
                style={{ background: "var(--surface)", border: "1.5px solid var(--mod-accent)", color: "var(--mod-accent)" }}>
                {n}
            </div>
            <div className="flex-1">
                <div className="font-bold text-[13.5px] mb-1" style={{ color: "var(--navy)" }}>{title}</div>
                <div className="text-[12.5px] leading-[1.55]" style={{ color: "var(--text-2)" }}>{children}</div>
            </div>
        </div>
    );
}

function InviteModal({ open, onClose, onJoined }) {
    const [invite, setInvite] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [copied, setCopied] = useState(false);
    const [status, setStatus] = useState("pending"); // pending | consumed | expired
    const [secondsLeft, setSecondsLeft] = useState(0);
    const pollRef = useRef(null);
    const tickRef = useRef(null);

    const generate = async () => {
        setLoading(true); setError("");
        try {
            const { data } = await api.post("/telegram/invites");
            setInvite(data);
            setStatus("pending");
            setSecondsLeft(data.expires_in_seconds || 900);
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open && !invite) generate();
        if (!open) {
            setInvite(null);
            setStatus("pending");
            setCopied(false);
            setError("");
            if (pollRef.current) clearInterval(pollRef.current);
            if (tickRef.current) clearInterval(tickRef.current);
        }
        // eslint-disable-next-line
    }, [open]);

    // Poll status
    useEffect(() => {
        if (!invite?.token || status !== "pending") return;
        pollRef.current = setInterval(async () => {
            try {
                const { data } = await api.get(`/telegram/invites/${invite.token}`);
                if (data.status === "consumed") {
                    setStatus("consumed");
                    clearInterval(pollRef.current);
                    setTimeout(() => { onJoined?.(); onClose(); }, 2000);
                } else if (data.status === "expired") {
                    setStatus("expired");
                    clearInterval(pollRef.current);
                }
            } catch {}
        }, 2500);
        return () => clearInterval(pollRef.current);
    }, [invite?.token, status, onJoined, onClose]);

    // Countdown
    useEffect(() => {
        if (!invite || status !== "pending") return;
        tickRef.current = setInterval(() => {
            setSecondsLeft((s) => {
                if (s <= 1) {
                    setStatus("expired");
                    clearInterval(tickRef.current);
                    return 0;
                }
                return s - 1;
            });
        }, 1000);
        return () => clearInterval(tickRef.current);
    }, [invite, status]);

    const copyLink = () => {
        if (invite?.deep_link) {
            navigator.clipboard.writeText(invite.deep_link);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        }
    };

    const fmtTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(13,20,33,.6)" }} onClick={onClose} data-testid="invite-modal">
            <div className="card-tpl w-full max-w-md p-7" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-1">
                    <h3 className="text-[20px] font-extrabold tracking-tight" style={{ color: "var(--navy)" }}>Aggiungi operatore</h3>
                    <button onClick={onClose} className="w-8 h-8 rounded-md grid place-items-center hover:bg-stone-100" data-testid="invite-close"><X size={16} /></button>
                </div>
                <p className="text-[13px] mb-5" style={{ color: "var(--text-2)" }}>
                    Invia questo link/QR all'operatore. Quando lo aprirà da Telegram, sarà autorizzato automaticamente.
                </p>

                {error && <div className="p-3 rounded-lg text-[13px] mb-3" style={{ background: "rgba(239,68,68,.1)", color: "var(--red)" }}>{error}</div>}

                {loading && <div className="text-center py-8 text-[13px]" style={{ color: "var(--muted)" }}>Generazione link in corso...</div>}

                {invite && status === "pending" && (
                    <>
                        <div className="card-tpl p-4 flex flex-col items-center mb-4" style={{ background: "var(--bg)" }}>
                            <img src={invite.qr_url} alt="QR Code" className="w-56 h-56" />
                            <div className="mt-3 text-[11px] mono uppercase tracking-wider flex items-center gap-1.5" style={{ color: "var(--muted)" }}>
                                <Clock size={12} /> Scade tra <span style={{ color: "var(--mod-accent)", fontWeight: 700 }}>{fmtTime(secondsLeft)}</span>
                            </div>
                        </div>

                        <a
                            data-testid="invite-open-telegram"
                            href={invite.deep_link}
                            target="_blank"
                            rel="noreferrer"
                            className="btn-grad w-full py-3 rounded-xl font-bold text-[14px] flex items-center justify-center gap-2 mb-2"
                        >
                            <Send size={15} /> Apri in Telegram
                        </a>

                        <button
                            data-testid="invite-copy"
                            onClick={copyLink}
                            className="w-full py-3 rounded-xl font-bold text-[13px] flex items-center justify-center gap-2"
                            style={{ background: "var(--surface)", border: "1px solid var(--line-2)", color: "var(--text)" }}
                        >
                            {copied ? <><Check size={14} style={{ color: "var(--green)" }} /> Copiato!</> : <><Copy size={14} /> Copia link</>}
                        </button>

                        <p className="text-[11.5px] text-center mt-4" style={{ color: "var(--muted)" }}>
                            ⏳ In attesa che l'operatore tocchi il link...
                        </p>
                    </>
                )}

                {status === "consumed" && (
                    <div className="text-center py-10" data-testid="invite-success">
                        <div className="w-16 h-16 rounded-full mx-auto mb-4 grid place-items-center" style={{ background: "var(--green-soft)" }}>
                            <Check size={28} style={{ color: "var(--green)" }} />
                        </div>
                        <h4 className="text-[18px] font-extrabold mb-1" style={{ color: "var(--green)" }}>Operatore connesso!</h4>
                        <p className="text-[13px]" style={{ color: "var(--text-2)" }}>L'operatore è stato registrato e può iniziare a usare il bot.</p>
                    </div>
                )}

                {status === "expired" && (
                    <div className="text-center py-8" data-testid="invite-expired">
                        <div className="w-16 h-16 rounded-full mx-auto mb-4 grid place-items-center" style={{ background: "rgba(245,158,11,.15)" }}>
                            <Clock size={26} style={{ color: "var(--amber)" }} />
                        </div>
                        <h4 className="text-[16px] font-extrabold mb-2" style={{ color: "var(--navy)" }}>Link scaduto</h4>
                        <p className="text-[13px] mb-4" style={{ color: "var(--text-2)" }}>Genera un nuovo link per riprovare.</p>
                        <button onClick={generate} className="btn-grad inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-bold text-[13px]">
                            <QrCode size={14} /> Nuovo link
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default function TelegramBot() {
    const { user } = useAuth();
    const [status, setStatus] = useState(null);
    const [operators, setOperators] = useState([]);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [s, o] = await Promise.all([api.get("/telegram/status"), api.get("/telegram/operators")]);
            setStatus(s.data);
            setOperators(o.data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const delOp = async (id) => {
        if (!window.confirm("Rimuovere operatore?")) return;
        await api.delete(`/telegram/operators/${id}`);
        load();
    };

    return (
        <div className="space-y-6" data-testid="telegram-page">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <h1
                        className="tracking-tight"
                        style={{
                            color: "var(--navy)",
                            fontFamily: "Geist, Outfit, sans-serif",
                            fontSize: "clamp(1.7rem, 2.8vw, 2.2rem)",
                            fontWeight: 200,
                            letterSpacing: "-0.035em",
                            lineHeight: 1.05,
                        }}
                    >
                        Bot <span style={{ fontWeight: 600 }}>Telegram</span>
                    </h1>
                    <p className="text-[13px] mt-1" style={{ color: "var(--text-2)" }}>
                        Inoltra i messaggi cliente al bot → l'AI crea commesse automaticamente.
                    </p>
                </div>
                {user?.role === "admin" && status?.configured && (
                    <button
                        data-testid="add-operator-btn"
                        onClick={() => setInviteOpen(true)}
                        className="btn-grad px-5 py-2.5 rounded-lg text-[13px] font-bold flex items-center gap-2"
                    >
                        <Plus size={14} /> Aggiungi operatore
                    </button>
                )}
            </div>

            {/* Platform bot status — NO bot username shown */}
            {!status?.configured ? (
                <div className="card-tpl p-6" style={{ borderLeft: "4px solid var(--orange)" }}>
                    <div className="flex items-start gap-3">
                        <AlertCircle size={20} style={{ color: "var(--orange)" }} />
                        <div>
                            <div className="font-bold text-[15px]" style={{ color: "var(--navy)" }}>Servizio Telegram non ancora attivo</div>
                            <p className="text-[13px] mt-1" style={{ color: "var(--text-2)" }}>
                                Il servizio è in configurazione. Contatta il supporto TotalPL.
                            </p>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="card-tpl p-5" style={{ borderLeft: "4px solid var(--green)" }}>
                    <div className="flex items-center gap-4 flex-wrap">
                        <div className="w-12 h-12 rounded-lg grid place-items-center" style={{ background: "var(--mod-accent-soft)" }}>
                            <Bot size={20} style={{ color: "var(--mod-accent)" }} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <span className="font-extrabold text-[16px]" style={{ color: "var(--navy)" }}>
                                    Servizio Telegram
                                </span>
                                <span className="mono text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded" style={{ background: "var(--green-soft)", color: "var(--green)" }}>● ATTIVO</span>
                            </div>
                            <p className="text-[12.5px] mt-0.5" style={{ color: "var(--text-2)" }}>
                                Pronto a ricevere messaggi dai tuoi operatori autorizzati
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Guide + Operators */}
            <div className="grid lg:grid-cols-2 gap-5">
                <div className="card-tpl p-6">
                    <div className="flex items-center gap-2 mb-5">
                        <BookOpen size={18} style={{ color: "var(--mod-accent)" }} />
                        <h3 className="text-[16px] font-extrabold" style={{ color: "var(--navy)" }}>Come funziona</h3>
                    </div>

                    <GuideStep n="1" title="Genera un link di invito">
                        Clicca <b style={{ color: "var(--mod-accent)" }}>+ Aggiungi operatore</b> in alto a destra. TotalPL crea un <b>link magico</b> + QR code valido 15 minuti.
                    </GuideStep>

                    <GuideStep n="2" title="Condividi il link con l'operatore">
                        Invia il link via WhatsApp/email/SMS, oppure mostragli il QR direttamente sullo schermo (se è di fianco a te).
                    </GuideStep>

                    <GuideStep n="3" title="L'operatore tocca il link">
                        Si apre Telegram, il bot lo accoglie e lo registra automaticamente. <b>Nessun copia/incolla, nessun Telegram ID.</b>
                    </GuideStep>

                    <GuideStep n="4" title="L'operatore inoltra messaggi cliente">
                        WhatsApp/SMS/email → tieni premuto → Inoltra al bot → l'AI Claude crea la commessa con dati estratti.
                    </GuideStep>
                </div>

                {/* Operators */}
                <div className="card-tpl p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-[16px] font-extrabold" style={{ color: "var(--navy)" }}>Operatori autorizzati</h3>
                        <span className="mono text-[11px] uppercase tracking-wider font-bold" style={{ color: "var(--muted)" }}>{operators.length} attivi</span>
                    </div>

                    {operators.length === 0 ? (
                        <div className="text-center py-10">
                            <div className="w-14 h-14 rounded-full mx-auto mb-3 grid place-items-center" style={{ background: "var(--bg)" }}>
                                <Bot size={22} style={{ color: "var(--muted-2)" }} />
                            </div>
                            <p className="text-[13px] mb-4" style={{ color: "var(--text-2)" }}>
                                Nessun operatore autorizzato. {user?.role === "admin" && "Aggiungi il primo per iniziare."}
                            </p>
                            {user?.role === "admin" && status?.configured && (
                                <button
                                    onClick={() => setInviteOpen(true)}
                                    className="btn-grad inline-flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-[13px]"
                                >
                                    <Plus size={14} /> Aggiungi operatore
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {operators.map((o) => (
                                <div key={o.id} data-testid={`op-${o.id}`} className="flex items-center gap-3 p-3 rounded-lg" style={{ background: "var(--bg)" }}>
                                    <div className="w-9 h-9 rounded-md grid place-items-center text-white font-bold text-[12px]" style={{ background: "var(--cobalt)" }}>
                                        {o.name.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-bold text-[13.5px]" style={{ color: "var(--navy)" }}>{o.name}</div>
                                        <div className="text-[10.5px]" style={{ color: "var(--muted)" }}>
                                            {o.telegram_username ? `@${o.telegram_username}` : "Operatore registrato"}
                                            {o.note && ` · ${o.note}`}
                                        </div>
                                    </div>
                                    {user?.role === "admin" && (
                                        <button onClick={() => delOp(o.id)} className="text-[11px]" style={{ color: "var(--red)" }} title="Rimuovi">
                                            <Trash2 size={13} />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* AI Reminder */}
            <div className="card-tpl p-5">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg grid place-items-center" style={{ background: "linear-gradient(135deg, var(--mod-accent), var(--mod-accent-deep))", color: "#fff" }}>
                        <Bot size={18} />
                    </div>
                    <div>
                        <h4 className="font-bold text-[14px]" style={{ color: "var(--navy)" }}>Personalizza l'AI per la tua tipografia</h4>
                        <p className="text-[13px] mt-1" style={{ color: "var(--text-2)" }}>
                            Più dettagli dai sulla tua azienda (specializzazione, reparti, parole urgenza) in <a href="/configurazione" className="font-bold" style={{ color: "var(--mod-accent)" }}>Configurazione</a>, più l'AI sarà precisa.
                        </p>
                    </div>
                </div>
            </div>

            <InviteModal open={inviteOpen} onClose={() => setInviteOpen(false)} onJoined={load} />
        </div>
    );
}
